/**
 * Project Dashboard page
 * Shows project stats, creative library with filtering, and actions
 */

import { ArrowLeft, Plus, Settings } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { CreativeGrid } from '@/components/creative/CreativeGrid';
import { ImportMetaButton } from '@/components/creative/ImportMetaButton';
import { ImportProgress } from '@/components/meta/ImportProgress';
import { StatsBar } from '@/components/project/StatsBar';
import { Button } from '@/components/ui/Button';
import { useProject, useCreatives, useCreativeStats, useTaxonomySchema } from '@/hooks';
import { useImportJobs } from '@/hooks/useImportJob';

// ============================================================================
// SKELETON COMPONENTS
// ============================================================================

function HeaderSkeleton() {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="space-y-2">
        <div className="h-3 w-20 bg-surface-subtle rounded animate-pulse" />
        <div className="h-10 w-64 bg-surface-subtle rounded animate-pulse" />
      </div>
      <div className="flex items-center gap-3">
        <div className="h-11 w-24 bg-surface-subtle rounded-pill animate-pulse" />
        <div className="h-11 w-36 bg-surface-subtle rounded-pill animate-pulse" />
      </div>
    </div>
  );
}

// ============================================================================
// PROJECT DASHBOARD COMPONENT
// ============================================================================

export function ProjectDashboard() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  // Data fetching
  const { data: project, isLoading: isLoadingProject } = useProject(projectId!);
  const { data: creatives, isLoading: isLoadingCreatives } = useCreatives(projectId);
  const stats = useCreativeStats(projectId);
  const { data: taxonomySchema } = useTaxonomySchema(projectId!);

  // Fetch active import jobs for this project
  const { data: importJobs } = useImportJobs(projectId);

  // Show active jobs and recently completed jobs (last 2 minutes)
  const TWO_MINUTES_MS = 2 * 60 * 1000;
  const visibleImportJobs = (importJobs ?? []).filter((job) => {
    // Always show active jobs
    if (job.status === 'queued' || job.status === 'processing') {
      return true;
    }
    // Show completed/partial/failed jobs for 2 minutes after completion
    if (job.status === 'completed' || job.status === 'partial' || job.status === 'failed') {
      const updatedAt = new Date(job.updatedAt).getTime();
      const now = Date.now();
      return (now - updatedAt) < TWO_MINUTES_MS;
    }
    return false;
  });

  // Loading state
  if (isLoadingProject) {
    return (
      <div className="space-y-8">
        <HeaderSkeleton />
        <StatsBar
          total={0}
          scale={0}
          fix={0}
          kill={0}
          pending={0}
          isLoading
        />
      </div>
    );
  }

  // Project not found
  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <h2 className="text-2xl font-bold text-primary mb-2">Project not found</h2>
        <p className="text-secondary mb-6">
          The project you&apos;re looking for doesn&apos;t exist or you don&apos;t have access.
        </p>
        <Button onClick={() => navigate('/projects')}>
          Back to Projects
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div>
          {/* Breadcrumb */}
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted hover:text-secondary transition-colors mb-2"
          >
            <ArrowLeft className="h-3 w-3" />
            All Projects
          </Link>

          {/* Title */}
          <h1 className="text-4xl font-black text-primary leading-tight tracking-tight">
            {project.name}
          </h1>

          {/* Brand name */}
          {project.brandName && (
            <p className="text-secondary mt-1">{project.brandName}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => navigate(`/projects/${projectId}/settings`)}
            leftIcon={<Settings className="h-4 w-4" />}
          >
            Settings
          </Button>
          <ImportMetaButton projectId={projectId!} />
          <Button
            onClick={() => navigate(`/projects/${projectId}/upload`)}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Upload Creative
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <StatsBar
        total={stats.total}
        scale={stats.scale}
        fix={stats.fix}
        kill={stats.kill}
        pending={stats.notAnalyzed}
        isLoading={stats.isLoading}
      />

      {/* Active Import Jobs */}
      {visibleImportJobs.length > 0 && (
        <div className="space-y-3">
          {visibleImportJobs.map((job) => (
            <ImportProgress key={job.jobId} job={job} />
          ))}
        </div>
      )}

      {/* Creative Library */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-primary">Creative Library</h2>
        <CreativeGrid
          creatives={creatives ?? []}
          isLoading={isLoadingCreatives}
          projectId={projectId!}
          taxonomySchema={taxonomySchema}
        />
      </div>
    </div>
  );
}
